// Typed data hooks for screens (WEB_PLAN.md §3.2, §3.10): the sync replica's raw rows decoded with the contracts
// models, each with its row meta, plus the derived views (visible people and stores, derived record stats, the
// metrics index) the domain/ functions need. See README.md in this folder.
//
// - Decoding is lazy and memoised per row object (rows are immutable; a changed row is a new object), so each row
//   version decodes once however many screens read it. Lists and derived views are built once per change for all
//   components together (lists.ts), so every component gets the same object until its collection changes.
// - `items` holds every live row, including ones an iPhone could not decode (`undecodable` says why: show them
//   read-only, never edit them). `values` holds only the decodable ones: what an iPhone has, so domain/ math run on
//   `values` gives the iPhone's numbers.

import { useMemo } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { SETTINGS_ID, type Collection } from '@/contracts/collections';
import type {
  ActivityPayload,
  AttachmentPayload,
  BadgePayload,
  CommPayload,
  CustomFieldPayload,
  DayLogPayload,
  DocumentPayload,
  GoalPayload,
  LeadSourcePayload,
  PayloadOf,
  PipelinePayload,
  RecordPayload,
  RoutePayload,
  SettingsPayload,
  TeamPayload,
  TemplatePayload,
  TerritoryPayload,
  UserPayload,
  VisitResultPayload,
} from '@/contracts/models';
import { startOfDay } from '@/domain/calendar';
import type { DerivedStats } from '@/domain/derived';
import type { MetricsIndex } from '@/domain/metrics';
import type { RecordLookups } from '@/domain/records';
import type { RepPosition } from '@/domain/team';
import type { Viewer } from '@/domain/visibility';
import { useRow, useRows } from '@/sync/hooks';
import {
  DEFAULT_SETTINGS,
  decodeItem,
  decodeRows,
  selectDerivedStats,
  selectMetricsIndex,
  selectRecordLookups,
  selectRepPositions,
  selectVisibleRecords,
  selectVisibleUsers,
  type DataItem,
  type DataList,
} from './lists';

export { DEFAULT_SETTINGS };
export type { DataItem, DataList };

// Every hook below returns the same object to every component until its inputs change (lists.ts computes each result
// once per distinct input, for all components), so results are safe `useMemo` / `memo` dependencies.

/** Any collection, decoded. The list keeps its identity until that collection changes. */
export function useCollection<C extends Collection>(collection: C): DataList<PayloadOf<C>> {
  const rows = useRows(collection);
  return decodeRows(collection, rows);
}

/** One decoded item (null when missing). */
export function useDataItem<C extends Collection>(collection: C, id: string | null | undefined): DataItem<PayloadOf<C>> | null {
  const row = useRow(collection, id);
  return row ? decodeItem(collection, row) : null;
}

export const useUsers = (): DataList<UserPayload> => useCollection('users');
export const useTeams = (): DataList<TeamPayload> => useCollection('teams');
export const useRecords = (): DataList<RecordPayload> => useCollection('records');
export const useActivities = (): DataList<ActivityPayload> => useCollection('activities');
export const useTerritories = (): DataList<TerritoryPayload> => useCollection('territories');
export const usePipelines = (): DataList<PipelinePayload> => useCollection('pipelines');
export const useVisitResults = (): DataList<VisitResultPayload> => useCollection('visitResults');
export const useLeadSources = (): DataList<LeadSourcePayload> => useCollection('leadSources');
export const useCustomFields = (): DataList<CustomFieldPayload> => useCollection('customFields');
export const useDayLogs = (): DataList<DayLogPayload> => useCollection('dayLogs');
export const useAttachments = (): DataList<AttachmentPayload> => useCollection('attachments');
export const useComms = (): DataList<CommPayload> => useCollection('comms');
export const useGoals = (): DataList<GoalPayload> => useCollection('goals');
export const useBadges = (): DataList<BadgePayload> => useCollection('badges');
export const useTemplates = (): DataList<TemplatePayload> => useCollection('templates');
export const useDocuments = (): DataList<DocumentPayload> => useCollection('documents');
export const useRoutes = (): DataList<RoutePayload> => useCollection('routes');

/** The settings item with its meta (null until it has synced). */
export function useSettingsItem(): DataItem<SettingsPayload> | null {
  return useDataItem('settings', SETTINGS_ID);
}

/** Company settings (defaults until the item has synced; best effort when undecodable). */
export function useSettings(): SettingsPayload {
  return useSettingsItem()?.value ?? DEFAULT_SETTINGS;
}

/** The signed-in member as domain/ wants it (null outside a company session). */
export function useViewer(): Viewer | null {
  const session = useAuth().session;
  const profileId = session?.profileId ?? null;
  const role = session?.role ?? null;
  return useMemo(() => (role ? { profileId, role } : null), [profileId, role]);
}

/** The viewer's own `users` item (null until it has synced: show "Waiting for your admin's profile…"). */
export function useMe(): DataItem<UserPayload> | null {
  const viewer = useViewer();
  return useDataItem('users', viewer?.profileId);
}

/** `visibleUsers` for the viewer (everyone outside a session or before the viewer's profile has synced). */
export function useVisibleUsers(): DataList<UserPayload> {
  const users = useUsers();
  const teams = useTeams();
  const viewer = useViewer();
  return selectVisibleUsers(users, teams, viewer?.profileId ?? null, viewer?.role ?? null);
}

/** `visibleRecords` for the viewer: not archived unless `includeArchived` (the Stores list toggle). */
export function useVisibleRecords(options: { includeArchived?: boolean } = {}): DataList<RecordPayload> {
  const records = useRecords();
  const users = useUsers();
  const teams = useTeams();
  const territories = useTerritories();
  const settings = useSettings();
  const viewer = useViewer();
  return selectVisibleRecords(
    records,
    users,
    teams,
    territories,
    settings,
    viewer?.profileId ?? null,
    viewer?.role ?? null,
    options.includeArchived ?? false,
  );
}

/** Per-record visit count, last visit / result, last activity and next scheduled, from the synced activities. */
export function useDerivedStats(): DerivedStats {
  return selectDerivedStats(useActivities());
}

/** The metrics index over every activity and record (metricValue, leaderboard, repSummary, goals, badges). */
export function useMetricsIndex(): MetricsIndex {
  return selectMetricsIndex(useActivities(), useRecords(), useVisitResults());
}

/**
 * Lookups for pin colors, legends, filters and sorting (`recordLookups`). Rebuilt when the data changes or the day
 * rolls over (days-since-visit colors), so `now` is never more than a day stale; pass `now` explicitly for live clocks.
 */
export function useRecordLookups(): RecordLookups {
  const stats = useDerivedStats();
  const visitResults = useVisitResults();
  const users = useUsers();
  const pipelines = usePipelines();
  // The local date is part of the key: the lookups rebuild on the first render after midnight.
  return selectRecordLookups(stats, visitResults, users, pipelines, startOfDay(new Date()).getTime());
}

/**
 * Last known rep positions from the day logs (lower-case user id). `presence` is as of the last day-log change; for a
 * live dot use `presenceOf(position.at, now)` with a ticking `now` (ui `useTicker`).
 */
export function useRepPositions(): ReadonlyMap<string, RepPosition> {
  return selectRepPositions(useDayLogs());
}
